import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, User, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { SearchedOrder, OrderSearchMode } from '@/supabase/order/types/orderSearch';
import { renderHighlighted } from '@/lib/highlightMatch';

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 처리일시를 KST 고정으로 표기 (브라우저 TZ 무관 — DepositRow.formatDepositDateTime 패턴).
function formatStatusTimestampKst(value: string): { short: string; full: string } | null {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const kst = new Date(d.getTime() + KST_OFFSET_MS);
  const y = kst.getUTCFullYear();
  const m = kst.getUTCMonth() + 1;
  const day = kst.getUTCDate();
  const hh = String(kst.getUTCHours()).padStart(2, '0');
  const mi = String(kst.getUTCMinutes()).padStart(2, '0');
  return { short: `${m}/${day} ${hh}:${mi}`, full: `${y}년 ${m}월 ${day}일 ${hh}:${mi}` };
}

interface SearchResultOrderCardProps {
  order: SearchedOrder;
  mode: OrderSearchMode;
  query: string;
  onOpen: (order: SearchedOrder) => void;
}

function SearchResultOrderCardImpl({ order, mode, query, onOpen }: SearchResultOrderCardProps) {
  const statusTimestamp = order.is_keep
    ? null
    : order.status === '입금 완료'
      ? order.paid_at
      : order.status === '출고 완료'
        ? order.shipped_at
        : null;
  const statusActionLabel = order.status === '출고 완료' ? '출고' : '입금';
  const statusTimeKst = statusTimestamp ? formatStatusTimestampKst(statusTimestamp) : null;

  // 매칭 사유 배지는 통합/all 모드에서만 — 단일 모드에선 자명해서 중복 노이즈 (디자인 스펙 §2, 안티패턴 #6).
  const showMatchBadges = mode === 'all';
  const matchedByNickname = order.matchTypes.includes('nickname');
  const matchedByProduct = order.matchTypes.includes('product');
  // 아이템 라인 하이라이트는 matchedItemIds 기준 — 모드 무관 (nickname 모드는 항상 빈 배열이라 자연히 미강조).
  const matchedItemIds = new Set(order.matchedItemIds);

  return (
    <Card
      role="button"
      tabIndex={0}
      className="cursor-pointer transition-colors duration-200 hover:border-indigo-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
      onClick={() => onOpen(order)}
      onKeyDown={(e) => {
        // 결과 카드 키보드 접근 — Enter/Space로 주문 열기 (codexloop PR3-R1 M7). Space는 스크롤 방지.
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.key === ' ') e.preventDefault();
          onOpen(order);
        }
      }}
    >
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            <span className="whitespace-nowrap">{order.order_date}</span>
            {/* 모든 모드에서 카드 소유 고객 표기 필수 — 닉네임 검색도 부분일치라
                여러 고객('민지' → '김민지'/'민지맘')이 섞일 수 있음. 하이라이트로 매칭 부분 표시. */}
            <span className="truncate text-base font-medium text-gray-700">
              {matchedByNickname ? renderHighlighted(order.nickname, query) : order.nickname}
            </span>
            {showMatchBadges && matchedByNickname && (
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                <User className="h-3 w-3 text-gray-500" aria-hidden="true" />
                닉네임 매칭
              </span>
            )}
            {showMatchBadges && matchedByProduct && (
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                <Package className="h-3 w-3 text-gray-500" aria-hidden="true" />
                상품 매칭
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {statusTimeKst && (
              <span
                className="flex items-center gap-1 text-xs font-medium text-gray-600 tabular-nums whitespace-nowrap"
                title={`${statusTimeKst.full} ${statusActionLabel} 처리`}
              >
                <Clock className="h-3 w-3 text-gray-400" aria-hidden="true" />
                <span className="sr-only">{statusActionLabel} 처리 시각 </span>
                {statusTimeKst.short}
              </span>
            )}
            <Badge
              className={cn({
                "bg-red-100 text-red-800": order.is_keep,
                "bg-amber-100 text-amber-800": !order.is_keep && order.status === '부분 입금',
                "bg-yellow-100 text-yellow-800": !order.is_keep && order.status === '입금 완료',
                "bg-green-100 text-green-800": !order.is_keep && order.status === '출고 완료',
              })}
            >
              {order.is_keep ? '킵' : order.status}
            </Badge>
          </span>
        </CardTitle>
        <CardDescription>주문 총액: {order.total_price.toLocaleString()}원</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-0.5 text-sm">
          {order.items?.map(item => {
            const isMatched = matchedItemIds.has(item.id);
            return (
              <li
                key={item.id}
                className={cn(
                  "-mx-2 flex justify-between rounded px-2 py-1 transition-colors",
                  isMatched && "bg-indigo-50",
                )}
              >
                <span className={cn(isMatched && "font-medium text-indigo-900")}>
                  {isMatched ? renderHighlighted(item.item_name, query) : item.item_name} ({item.quantity}개)
                </span>
                <span className={cn("tabular-nums", isMatched && "font-medium text-indigo-900")}>
                  {(item.price * item.quantity).toLocaleString()}원
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export const SearchResultOrderCard = memo(SearchResultOrderCardImpl);
